/**
 * Table — scroll container plus shared data-table styling.
 *
 * Deliberately thin: callers still write their own <thead>/<tbody>, because the
 * portal's eight tables differ in every way that matters. What they share is the
 * look and the horizontal scroll container, and that is all this provides.
 *
 * Cell modifiers (numeric, actions, truncate) live in table-cells.ts, which
 * keeps this file exporting components only so Fast Refresh works.
 */

import type { ReactNode, TableHTMLAttributes } from 'react';
import styles from './table.module.css';

interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  /**
   * Drop the surface, border and radius, keeping only the horizontal scroll.
   *
   * For a table that already sits inside a card — the admin screens wrap header,
   * table and pagination in one panel — the default chrome would draw a second
   * card inside the first.
   */
  flush?: boolean;
  className?: string;
  children: ReactNode;
}

export function Table({ flush, className, children, ...rest }: TableProps) {
  return (
    <div className={[styles.scroll, flush ? styles.flush : null].filter(Boolean).join(' ')}>
      <table className={[styles.table, className].filter(Boolean).join(' ')} {...rest}>
        {children}
      </table>
    </div>
  );
}

/** Empty state, sized to span the whole table. */
export function TableEmpty({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className={styles.empty}>
        {children}
      </td>
    </tr>
  );
}
